import React, { useEffect, useRef, useState } from "react";
import { motion } from 'framer-motion';
import { Fade } from "react-awesome-reveal";
import { MdChevronRight } from "react-icons/md";

import "../../styling.css";
import BackgroundCover from "../../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../../components/TitleWithDescription/TitleWithDescription";
import { debounce, modifyStyles, resetDefaultStyles } from "../../../utils/utilFunctions";
import { isMobile } from "../../../utils/appUtils";
import { COMPONENT_COLORS } from '../../../utils/componentColors';
import Title from "../../../components/Title/Title";
import Collection from "../../../components/Collection/Collection";
import { useLanguage } from "../../../context/LanguageContext";
import { tourmalineTemplateTranslations } from "../../../translations/tourmalineTemplate";

function TourmalineCollection() {
    const { language } = useLanguage();
    const t = tourmalineTemplateTranslations[language];
    const [markdownContent, setMarkdownContent] = useState('');
    const colorFont = COMPONENT_COLORS.TEXT.LIGHT;
    const defaultColorFont = COMPONENT_COLORS.TEXT.DARK;
    const backgroundImageRef = useRef(null);

    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerChildren = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    // Holds which frequency is currently expanded (if any)
    const [expandedFrequency, setExpandedFrequency] = useState<string | null>(null);

    // Dummy data for the new frequencies with clickable expansion
    const getStandardFrequencies = (language: string) => [
        {
            id: "T555",
            title: language === 'en' ? "DISCOVERING NEW TERRITORIES" : "ENTDECKUNG NEUER TERRITORIEN",
            content: language === 'en'
                ? "Mathematics, and with mathematics all science, started with the number 5. The 5 fingers of our hands were the first counting aid and are the reason for our decimal mathematical system. 5 stands for the starting into science, for the discovery of all new and for progress in general.\n\nThe frequency 555 is the symbol for discovery, for the urge to conquer the unknown.\nIt is the frequency for those who have the courage to change the blueprint of their life.\nWho are looking for the essential.\nFor the decision to reach out for \"the ideal thing\".\nFor adding a new window inside our mind with a new view and a different horizon.\nFor leaving the life of a caterpillar to become a butterfly."
                : "Die Mathematik begann mit der Zahl 5 und mit ihr alle Wissenschaften. Die 5 Finger an unseren Händen waren die erste Zählhilfe und sind der Grund für unser dezimales mathematisches System. 5 steht für den Einstieg in die Wissenschaft, für die Erschließung von allem Neuen und für den Fortschritt im Allgemeinen.\n\nDie Frequenz 555 ist das Symbol für Entdeckungen, für den Drang das Unbekannte zu erobern. Es ist die Frequenz für diejenigen, die den Mut haben, die Blaupause ihres Lebens zu verändern. Die auf der Suche nach dem Wesentlichen sind. Für die Entscheidung, nach dem \"Idealen\" zu greifen. Für das Hinzufügen eines neuen Fensters in unserem Geist mit einer neuen Sichtweise und einem anderen Horizont. Die Frequenz für jene die es wagen das Lebens als Raupe aufzugeben, um ein Schmetterling zu werden.",
            certificate: "/images/freq_certs/cert 555 long.PNG"
        },
        {
            id: "T696",
            title: language === 'en' ? "THE MAGIC POWER OF ELVES AND FAIRIES" : "DIE MAGISCHE KRAFT DER ELFEN UND FEEN",
            content: language === 'en'
                ? "According to the founder of numerology, the ancient Greek philosopher Pythagoras, the number 6 is the most perfect number of all. 6 is at the same time the sum and the product of the first 3 numbers. 1 + 2 + 3 = 6, and 1 x 2 x 3 = 6. The number 6 stands symbolically for absolute perfection and harmony. In the numerology of the Cabbala, 6 stands for beauty.\n\n696 is the frequency of beauty, harmony and perfection.\n\nOne of the big forces, which holds this creation together like gravity the universe, is the power of beauty. The craving for harmony and love is the main engine that drives the mechanics of life on this planet. Beauty is the reward for the burden of life. To shine beauty into the darkness of our existence is the most heroic action. Beauty, harmony and physical perfection are the forebodes and the promise, that at the end of evolution the absolute good will surmount all evil and show its face."
                : "Nach dem Begründer der Numerologie, dem altgriechischen Philosophen Pythagoras, ist die Zahl 6 die vollkommenste Zahl von allen. 6 ist gleichzeitig die Summe und das Produkt der ersten 3 Zahlen. 1 + 2 + 3 = 6, und 1 x 2 x 3 = 6. Die Zahl 6 steht symbolisch für absolute Vollkommenheit und Harmonie. In der Numerologie der Kabbala steht die 6 für die Schönheit.\n\n696 ist die Frequenz von Schönheit, Harmonie und Vollkommenheit.\n\nEine der großen Kräfte, die diese Schöpfung zusammenhält wie die Schwerkraft das Universum, ist die Kraft der Schönheit. Das Verlangen nach Harmonie und Liebe ist der Hauptmotor, der die Mechanismen des Lebens auf diesem Planeten antreibt. Schönheit ist die Belohnung für die Last des Lebens. Schönheit in die Dunkelheit unserer Existenz zu bringen, ist die heldenhafteste Tat. Schönheit, Harmonie und körperliche Vollkommenheit sind die Vorboten und das Versprechen, dass am Ende der Evolution das absolut Gute alles Böse überwinden und sein Gesicht zeigen wird.",
            certificate: "/images/freq_certs/cert 696 long.PNG"
        },
        {
            id: "T777",
            title: language === 'en' ? "THE ART OF SUCCESS" : "DIE KUNST DES ERFOLGS",
            content: language === 'en'
                ? "Seven is the number which appears most often as a symbolic figure in all ancient mythologies. It usually stands for the connection between the Divine and the Cosmos. Also the school of Pythagoras defines the meaning of 7 as the unity of nature with the metaphysics. The physical figure 4, added to the spiritual figure 3, gives 7 (4 +3 = 7). In the old Cabbalistic numerology, 7 stands for success, for victory.\n\nSo 7 stands symbolically for the element which is the bridge between our physical and our metaphysical nature, which is our soul. Our soul, or self, is the element which is part of both worlds.\n\nCombining the expression of \"Self\" with the cabbalistic meaning of success, the symbolic meaning of 7 as used by Niveau élevé, is the finding of our own self and the success of our own aims in life.\n\n777 is the number for all those who want to learn the art of success of one's own targets, independent from our surroundings, the victory over circumstances, the implementation of what we ourselves truly want to achieve."
                : "7 ist die Zahl, die in allen alten Mythologien am häufigsten als symbolische Figur auftaucht. Sie steht gewöhnlich für die Verbindung zwischen dem Göttlichen und dem Kosmos. Auch die Schule des Pythagoras definiert die Bedeutung der 7 als die Einheit der Natur mit der Metaphysik. Die physische Zahl 4, addiert mit der geistigen Zahl 3, ergibt 7 (4 +3 = 7). In der alten kabbalistischen Numerologie steht die 7 für den Erfolg, für den Sieg.\n\n7 steht auch symbolisch für das Element, das die Brücke zwischen unserer physischen und unserer metaphysischen Natur bildet, für unsere Seele. Unsere Seele, oder unser Selbst, ist das Element, das Teil beider Welten ist.\n\nKombiniert man den Ausdruck \"Selbst\" mit der kabbalistischen Bedeutung von Erfolg, so ist die symbolische Bedeutung der 7, wie sie von Niveau élevé verwendet wird, das Finden unseres eigenen Selbst und der Erfolg unserer eigenen Ziele im Leben.\n\n777 ist die Zahl für all jene, die die Kunst des Könnens, das Erreichen der eigenen Ziele lernen wollen.",
            certificate: "/images/freq_certs/cert 777 long.PNG"
        },
        {
            id: "T888",
            title: language === 'en' ? "THE MECHANISM OF LUCK" : "DER MECHANISMUS DES GLÜCKS",
            content: language === 'en'
                ? "In all Far-East Asian cultures 8 stands for luck and wealth. Even in the old Jewish cabbalistic numerology 8 stands for richness and splendour.\n\nBut Luck is not a self-propelled mechanism, independent from our attitude and action. Luck needs certain conditions to manifest itself. We can very well influence the quantity of good luck or bad-luck which manifests itself in our lives. Qualities like courage, readiness to take risks, self-confidence, optimism, clarity of our own character values, the firm belief in our own capacities and our own success, determination and persistence, are creating the ideal conditions for luck to manifest. Pessimism, deviation from our own conviction, doubts and fear are the ideal nourishing ground for bad luck.\n\nThe figure 888 is meant for all those who want to take their luck in their own hands and work actively on their own character and capacity to become a perfect instrument for luck to manifest in their life. Luck is not at all out of our control."
                : "In allen fernostasiatischen Kulturen steht die 8 für Glück und Reichtum. Auch in der alten jüdischen kabbalistischen Numerologie steht 8 für Reichtum und Pracht.\n\nDoch das Glück ist kein selbsttätiger Mechanismus, unabhängig von unserer Einstellung und unserem Handeln. Das Glück braucht bestimmte Bedingungen, um sich zu manifestieren. Wir können sehr wohl die Menge an Glück oder Unglück beeinflussen, die sich in unserem Leben manifestiert. Eigenschaften wie Mut, Risikobereitschaft, Selbstvertrauen, Optimismus, Klarheit über die eigenen Charakterwerte, der feste Glaube an die eigenen Fähigkeiten und den eigenen Erfolg, Entschlossenheit und Beharrlichkeit schaffen die idealen Voraussetzungen dafür, dass sich Glück manifestiert. Pessimismus, Abweichung von der eigenen Überzeugung, Zweifel und Angst sind der ideale Nährboden für Unglück.\n\nDie Zahl 888 ist für all jene gedacht, die ihr Glück selbst in die Hand nehmen und aktiv an ihrem eigenen Charakter und ihren Fähigkeiten arbeiten wollen, damit sich das Glück in ihrem Leben manifestiert. Glück liegt keineswegs völlig außerhalb unserer Kontrolle.",
            certificate: "/images/freq_certs/cert 888 long.PNG"
        },
        {
            id: "T999",
            title: language === 'en' ? "TAKING THE RIGHT DECISION – THE INTUITION" : "DIE RICHTIGE ENTSCHEIDUNG TREFFEN - DIE INTUITION",
            content: language === 'en'
                ? "In Hindu-mythology 99 stands for the totality of the universe. It represents the 7 main principles of this creation, each of which again divides into 7 aspects. 7 x 7 = 49. These 7 main principles are the same for the involution and the evolution. 49 + 49 = 98. To that the old Vedas have added the figure 1 for the divine, which stands above the involution or evolution. 98 + 1 = 99.\n\n999 is the number symbolizing the totality, it stands for the full knowledge, the undivided essential.\n\nKnowledge is no product of our mind. Thoughts cannot create knowledge. They only express knowledge in well-known terms, so that we can comprehend its message. Knowledge exists already by itself. It does not need thoughts to give it a shape. There are other mechanisms which can express knowledge. Intuition is the most direct method. Thoughts, by trying to press knowledge in comprehensible standard terms, have the tendency to distort it. Intuition, the thoughtless way of experiencing knowledge, grasps it directly without any intermediary.\n\n999 is the number for those who want to find the right path in life, who want to take the right decisions, who seek deep knowledge and go for the essential."
                : "In der Hindu-Mythologie steht 99 für die Gesamtheit des Universums. Diese Zahl steht für die 7 Hauptprinzipien dieser Schöpfung, von denen jedes wiederum in 7 Aspekte unterteilt ist. 7 x 7 = 49. Diese 7 Hauptprinzipien sind die gleichen für die Involution und die Evolution. 49 + 49 = 98. Dazu haben die alten Veden die Zahl 1 für das Göttliche hinzugefügt, das sowohl über der Involution als auch der Evolution steht. 98 + 1 = 99.\n\n999 ist die Zahl, die die Gesamtheit symbolisiert. Sie steht für das volle Wissen, das ungeteilte Wesentliche.\n\nWissen ist kein Produkt unseres Verstandes. Gedanken können kein Wissen erschaffen. Sie drücken das Wissen nur in bekannten Begriffen aus, damit wir seine Botschaft begreifen können. Das Wissen existiert bereits von selbst. Es braucht auch keine Gedanken, um ihm eine Form zu geben. Es gibt andere Mechanismen, die Wissen ausdrücken können. Die Intuition ist die direkteste Methode. Gedanken, die versuchen, Wissen in verständliche Standardbegriffe zu pressen, haben die Tendenz, es zu verzerren. Die Intuition, die gedankenlose Art und Weise, Wissen zu erfahren, erfasst es direkt und ohne Zwischenschaltung eines Vermittlers.\n\n999 ist die Zahl für diejenigen, die den richtigen Weg im Leben finden wollen, die die richtigen Entscheidungen treffen wollen, die tiefes Wissen suchen und sich auf das Wesentliche konzentrieren.",
            certificate: "/images/freq_certs/cert 999 long.PNG"
        }
    ];

    const standardFrequencies = getStandardFrequencies(language);

    const [expandedCertificate, setExpandedCertificate] = useState<string | null>(null);

    const toggleFrequency = (id: string) => {
        setExpandedFrequency(expandedFrequency === id ? null : id);
    };

    const openCertificate = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedCertificate(id);
    };

    const closeCertificate = () => {
        setExpandedCertificate(null);
    };

    // Certificate Modal Component
    const CertificateModal = ({ certificateUrl, onClose }: { certificateUrl: string; onClose: () => void }) => {
        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <div className="relative max-w-6xl w-full h-auto">
                    <button
                        onClick={onClose}
                        className="absolute -top-10 right-0 text-white hover:text-gray-300"
                    >
                        Close ×
                    </button>
                    <img
                        src={certificateUrl}
                        alt="Frequency Certificate"
                        className="w-full h-auto rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>
        );
    };

    useEffect(() => {
        const fetchMarkdownContent = async () => {
            try {
                const response = await fetch('/tourmaline.md');
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const text = await response.text();
                setMarkdownContent(text);
            } catch (error) {
                console.error('Error fetching markdown:', error);
                setMarkdownContent('Failed to load content. Please try again later.');
            }
        };

        fetchMarkdownContent();
    }, []);

    useEffect(() => {
        const headerEl: HTMLElement | null = document.querySelector(".header");
        const tabsEl: HTMLElement | null = document.querySelector(".tabs");
        const tabContainerDivEl: HTMLElement | null = document.querySelector(".tabs-container");
        const watchContainerEl: HTMLElement | null = document.querySelector(".watch-container");

        const backgroundImageDiv = backgroundImageRef.current;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    (window as any).isIntersectingBg = true;
                } else {
                    (window as any).isIntersectingBg = false;
                }
            });
        });

        if (backgroundImageDiv) {
            observer.observe(backgroundImageDiv);
        }

        return () => {
            (window as any).isIntersectingBg = undefined;
            resetDefaultStyles(headerEl, tabsEl, watchContainerEl, tabContainerDivEl, colorFont, defaultColorFont);
            if (backgroundImageDiv) {
                observer.unobserve(backgroundImageDiv);
            }
        };
    }, [colorFont, defaultColorFont]);

    const getSection = (sectionName: string): string => {
        const sectionRegex = new RegExp(`## ${sectionName}([\\s\\S]*?)(?=## |$)`, 'i');
        const match = markdownContent.match(sectionRegex);
        return match ? match[1].trim() : '';
    };

    const getCollectionItems = (language: string) => [
        {
            id: "1",
            name: language === 'en' ? "Lucky Star bracelet" : "Lucky Star Armband",
            description: language === 'en' ? "Pink tourmaline with special frequency, 18k white gold" : "Pink Turmalin mit spezieller Frequenz, 750 Weißgold",
            image: "/images/jewellery_tourmaline_grid_1_bracelet1.png"
        },
        {
            id: "2",
            name: language === 'en' ? "Lucky Star bracelet" : "Lucky Star Armband",
            description: language === 'en' ? "Pink tourmaline with special frequency, 18k rose gold" : "Pinker Turmalin mit spezieller Frequenz, 750 Roségold",
            image: "/images/jewellery_tourmaline_grid_2_bracelet2.png"
        },
        {
            id: "3",
            name: language === 'en' ? "Lucky Star bracelet" : "Lucky Star Armband",
            description: language === 'en' ? "Green tourmaline with special frequency, 18k yellow gold" : "Grüner Turmalin mit spezieller Frequenz, 750 Gelbgold",
            image: "/images/jewellery_tourmaline_grid_3_bracelet3.png"
        },
        {
            id: "4",
            name: language === 'en' ? "Lucky Star Triplet bracelet" : "Armband Lucky Star Triplet",
            description: language === 'en' ? "Three pink tourmalines with special frequency, 18k rose gold" : "Drei pinke Turmaline mit spezieller Frequenz, 750 Roségold",
            image: "/images/jewellery_tourmaline_grid_4_bracelet4.png"
        },
        {
            id: "5",
            name: language === 'en' ? "Lucky Star Spiral bracelet" : "Glücksstern Spiralarmband",
            description: language === 'en' ? "Pink tourmalines with special frequency, 18k rose gold" : "Pinke Turmaline mit spezieller Frequenz, 750 Roségold",
            image: "/images/jewellery_tourmaline_grid_5_bracelet5.png"
        },
        {
            id: "6",
            name: language === 'en' ? "Lucky Star Spiral bracelet" : "Glücksstern Spiralarmband",
            description: language === 'en' ? "Pink and green tourmalines with special frequency, 18k yellow gold" : "Pinke und grüne Turmaline mit spezieller Frequenz, 750 Gelbgold",
            image: "/images/jewellery_tourmaline_grid_6_bracelet6.png"
        },
        {
            id: "7",
            name: language === 'en' ? "Lucky Star bracelet" : "Lucky Star-Armband",
            description: language === 'en' ? "Pink tourmalines with special frequency, 18k white gold" : "Pink Turmaline mit spezieller Frequenz, 750 Weißgold",
            image: "/images/jewellery_tourmaline_grid_7_bracelet7.png"
        },
        {
            id: "8",
            name: language === 'en' ? "Lucky Star necklace" : "Lucky Star Halskette",
            description: language === 'en' ? "Green tourmaline with special frequency, 18k white gold" : "Grüner Turmalin mit spezieller Frequenz, 750 Weißgold",
            image: "/images/jewellery_tourmaline_grid_8_necklace1.png"
        },
        {
            id: "9",
            name: language === 'en' ? "Lucky Star necklace" : "Lucky Star Halskette",
            description: language === 'en' ? "Pink tourmaline with special frequency, 18k white gold" : "Pinker Turmalin mit spezieller Frequenz, 750 Weißgold",
            image: "/images/jewellery_tourmaline_grid_9_necklace2.png"
        },
        {
            id: "10",
            name: language === 'en' ? "Compass pendant" : "Kompass-Anhänger",
            description: language === 'en' ? "Green tourmaline, 18k yellow gold" : "Grüner Turmalin, 750 Gelbgold",
            image: "/images/jewellery_tourmaline_grid_10_pendant1.png"
        },
        {
            id: "11",
            name: language === 'en' ? "Compass pendant with diamonds" : "Kompass-Anhänger mit Diamanten",
            description: language === 'en' ? "Green tourmaline, Niveau Elevé special cut diamonds, 18k yellow gold" : "Grüner Turmalin, Diamanten im Brillantschliff, 750 Gelbgold",
            image: "/images/jewellery_tourmaline_grid_11_pendant2.png"
        },
        {
            id: "12",
            name: language === 'en' ? "Compass pendant with diamonds" : "Kompass-Anhänger mit Diamanten",
            description: language === 'en' ? "Pink tourmaline, Niveau Elevé special cut diamonds, 18k yellow gold" : "Pinker Turmalin, Diamanten im Brillantschliff, 750 Gelbgold",
            image: "/images/jewellery_tourmaline_grid_12_pendant3.png"
        },
        {
            id: "13",
            name: language === 'en' ? "One Of A Kind Tourmaline ring" : "Turmalin-Solitär Ring",
            description: language === 'en' ? "Green tourmaline, princess cut diamonds, 18k white gold" : "Grüner Turmalin, Diamanten im Prinzesscut Schliff, 750 Weißgold",
            image: "/images/jewellery_tourmaline_grid_13_ring2.png"
        },
        {
            id: "14",
            name: language === 'en' ? "Element bracelet with five tourmalines" : "Magie-Band mit fünf Turmalinen",
            description: language === 'en' ? "Five pink tourmalines, Topaz, stainless steel" : "Fünf pinke Turmaline, Topas, Edelstahl",
            image: "/images/jewellery_tourmaline_grid_15_topazbracelet2.png"
        },
        {
            id: "15",
            name: language === 'en' ? "Element bracelet with five tourmalines" : "Magie-Band mit fünf Turmalinen",
            description: language === 'en' ? "Five pink and green tourmalines, Topaz, stainless steel" : "Fünf pinke und grüne Turmaline, Topas, Edelstahl",
            image: "/images/jewellery_tourmaline_grid_15_topazbracelet3.png"
        },
        {
            id: "16",
            name: language === 'en' ? "Lucky Star ring" : "Ring Lucky Star",
            description: language === 'en' ? "Pink and green tourmalines with special frequency, 18k yellow gold" : "Pinker und grüner Turmalin mit spezieller Frequenz, 750 Gelbgold",
            image: "/images/jewellery_tourmaline_grid_ring1.png"
        }
    ];

    const renderContent = () => {
        const fadeDuration = 2000;

        return (
            <>
                {/* Tourmaline Frequencies */}
                <div className="py-4 md:py-8">
                    <TitleWithDescription
                        title={{
                            firstLine: t.frequenciesTitle,
                            secondLine: t.frequenciesSubtitle
                        }}
                        description={t.frequenciesDescription}
                        layout="row"
                        className="px-4 md:px-8 lg:px-24"
                    />
                </div>

                {/* Vibrate in Frequency */}
                <TitleWithDescription
                    title={{
                        firstLine: t.oscillationTitle,
                        secondLine: t.oscillationSubtitle
                    }}
                    description={t.oscillationDescription}
                    layout="column"
                    image={{
                        src: "/images/jewellery_placeholder.png",
                        alt: "Tourmaline jewelry piece",
                        position: "left",
                        className: "w-full md:w-auto md:max-w-[90%] h-auto"
                    }}
                    className="bg-gradient-to-br from-[#E1D8D3] via-[#E0DDDD] to-[#E0DEDF] px-4 md:px-8 lg:px-24"
                />

                {/* Frequencies list */}
                <Fade duration={fadeDuration}>
                    <section className="bg-white py-8 md:py-16 px-4 md:px-8 lg:px-24">
                        <Title
                            firstLine={t.standardFrequenciesTitle}
                            secondLine={t.standardFrequenciesSubtitle}
                            className="mb-8"
                        />
                        <motion.div
                            className="space-y-4"
                            variants={staggerChildren}
                            initial="initial"
                            whileInView="animate"
                            viewport={{ once: true }}
                        >
                            {standardFrequencies.map((freq) => (
                                <motion.div
                                    key={freq.id}
                                    className="border-b border-gray-300 py-4"
                                    variants={fadeIn}
                                >
                                    <div
                                        className="flex items-center cursor-pointer justify-between"
                                        onClick={() => toggleFrequency(freq.id)}
                                    >
                                        <div className="flex items-center w-full">
                                            <MdChevronRight
                                                className={`w-5 h-5 transition-transform duration-300 ${expandedFrequency === freq.id ? 'rotate-90' : ''}`}
                                            />
                                            <div className="font-bold text-lg ml-2">
                                                {freq.id}
                                            </div>
                                            <div className="ml-4">
                                                {freq.title}
                                            </div>
                                        </div>
                                    </div>
                                    {expandedFrequency === freq.id && (
                                        <div className="mt-2 text-gray-600 pl-[44px]">
                                            <div className="mb-4">
                                                {freq.content}
                                            </div>
                                            <div className="mt-4">
                                                <img
                                                    src={freq.certificate}
                                                    alt={`${freq.id} Frequency Certificate`}
                                                    className="w-48 h-auto cursor-pointer hover:opacity-90 transition-opacity rounded shadow-md"
                                                    onClick={(e) => openCertificate(freq.id, e)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </section>
                </Fade>

                {/* Inner Compass */}
                <div className="relative w-full py-8 md:py-16 bg-gradient-to-br from-[#E1D8D3] via-[#E0DDDD] to-[#E0DEDF]">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col md:flex-row items-center max-w-7xl mx-auto">
                            {/* Left Text Block */}
                            <div className="md:flex-1 mb-8 md:mb-0 md:text-right md:pr-4">
                                <h2 className="text-3xl font-light mb-4">{t.innerCompassTitle}</h2>
                                <h2 className="text-3xl italic mb-6">{t.innerCompassSubtitle}</h2>
                            </div>

                            {/* Center Image */}
                            <div className="flex-none mb-8 md:mb-0">
                                <img
                                    src="/images/jewellery_collections_tournaline.png"
                                    alt="Tourmaline Crystal"
                                    className="w-[70px] h-auto rotate-180"
                                />
                            </div>

                            {/* Right Text Block */}
                            <div className="md:flex-1 mt-8 md:mt-0 md:pl-4 text-center md:text-left">
                                <h3 className="text-xl font-medium mb-4">{t.vikingsTitle}</h3>
                                <p className="text-sm leading-relaxed">
                                    {t.vikingsDescription}
                                </p>
                                <a href="#" className="text-sm mt-4 inline-block hover:underline">
                                    {t.discoverMore}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lucky Star */}
                <TitleWithDescription
                    title={{
                        firstLine: t.luckyStarTitle,
                        secondLine: t.luckyStarSubtitle
                    }}
                    description={t.luckyStarDescription}
                    layout="column"
                    image={{
                        src: "/images/jewellery_tourmaline_sub_luckystar.png",
                        alt: "Tourmaline Crystal",
                        position: "right",
                        className: "w-48 h-auto justify-self-center"
                    }}
                    className="bg-gradient-to-r from-[#DD8288] to-[#8A2556] text-white px-4 md:px-8 lg:px-40"
                />

                <TitleWithDescription
                    title={{
                        firstLine: t.napoleonTitle,
                        secondLine: t.napoleonSubtitle
                    }}
                    description={t.napoleonDescription}
                    layout="row"
                    className="px-4 md:px-8 lg:px-24"
                />

                {/* The Collection */}
                <div className="pb-8 md:pb-16">
                    <Collection
                        items={getCollectionItems(language)}
                        clickable={false}
                    />
                </div>
            </>
        );
    };

    return (
        <div className="min-h-screen bg-white">
            {expandedCertificate && (
                <CertificateModal
                    certificateUrl={standardFrequencies.find(f => f.id === expandedCertificate)?.certificate || ''}
                    onClose={closeCertificate}
                />
            )}
            <BackgroundCover
                backgroundHeight={isMobile() ? 400 : undefined}
                imagePosition="center"
                backgroundImage="/images/jewellery_tourmaline_background.jpg"
                isWatchPage={true}
            >
                <div className="absolute top-1/2 left-[10%] md:left-[100px] -translate-y-1/2 z-10">
                    <Title
                        firstLine={t.mainTitle}
                        secondLine={t.mainSubtitle}
                        className="text-white mt-[-80px]"
                        animate={true}
                    />
                </div>
            </BackgroundCover>
            <div className="w-full">
                {renderContent()}
            </div>
        </div>
    );
}

export default TourmalineCollection;